import { observable, type IObservableValue } from 'mobx';
import { toast } from 'react-toastify';
import { confirmDialog } from 'dialogs';
import type { EditableEntity } from './EditableEntity';
import { toError } from 'helpers/toError';

/**
 * Caller-provided messages for the saving toasts. Kept simple — fired
 * once per `submit()` outcome. If a caller needs entity-aware variants
 * (e.g. "created" vs "updated"), it can construct messages at the call
 * site that builds the EditModel.
 */
export interface EditModelOptions {
  /**
   * Whether the dialog opens in create or edit mode. Used as the
   * fallback for `EditModel.isCreating` while the entity is still
   * loading — once the entity is available the model defers to it.
   */
  readonly initialMode: 'create' | 'edit';
  readonly successMessage: string;
  readonly errorMessagePrefix?: string;
}

/**
 * Generic edit-lifecycle orchestrator for an `EditableEntity<D>`.
 *
 * Holds two orthogonal state observables (not deep — both store
 * sentinel literals alongside the entity / Error so MobX shouldn't
 * proxy them):
 * - `_state`: 'loading' | T | Error — what the editable IS at this
 *   moment, from the load lifecycle's perspective.
 * - `_savingState`: 'idle' | 'saving' | Error — what the editable is
 *   DOING in the save lifecycle. Mutating this is independent of
 *   `_state`; while a save is in flight, the entity is still readable.
 *
 * Errors surface differently per the UI's choice: load errors are
 * exposed via `loadError` for inline display; save success/error fire
 * react-toastify toasts directly here.
 *
 * The entity is constructed lazily via `entityFactory(data)` after the
 * initialize promise resolves, so the caller doesn't need to invent a
 * pre-load placeholder data value.
 *
 * Load is kicked off from the constructor and exposed as a `loaded`
 * Promise so tests can await readiness and the modal doesn't need to
 * remember to start the load itself.
 */
export class EditModel<T extends EditableEntity<Loaded>, Loaded> {
  private readonly _initialize: () => Promise<Loaded>;
  private readonly _save: (entity: T) => Promise<Loaded>;
  private readonly _entityFactory: (loaded: Loaded) => T;
  private readonly _options: EditModelOptions;

  private readonly _state: IObservableValue<T | 'loading' | Error>;
  private readonly _savingState: IObservableValue<'idle' | 'saving' | Error>;

  readonly loaded: Promise<void>;

  constructor(initialize: () => Promise<Loaded>, save: (entity: T) => Promise<Loaded>, entityFactory: (loaded: Loaded) => T, options: EditModelOptions) {
    this._initialize = initialize;
    this._save = save;
    this._entityFactory = entityFactory;
    this._options = options;
    this._state = observable.box<T | 'loading' | Error>('loading', {
      deep: false,
    });
    this._savingState = observable.box<'idle' | 'saving' | Error>('idle', {
      deep: false,
    });
    this.loaded = this._load();
  }

  private _load = async (): Promise<void> => {
    try {
      const loaded = await this._initialize();
      this._state.set(this._entityFactory(loaded));
    } catch (err) {
      this._state.set(toError(err));
    }
  };

  /**
   * The "Close" button / Esc / backdrop handler. If the entity hasn't
   * loaded or has no unsaved changes, closes immediately. Otherwise
   * asks the user to confirm discarding their edits and closes only on
   * confirmation. The caller passes its own `close` callback (usually
   * the `cancel` from `waitDialog`) so the model stays presentation-
   * agnostic about HOW dialogs close.
   */
  cancel = async (close: () => void): Promise<void> => {
    const entity = this.entity;
    if (!entity || !entity.isDirty) {
      close();
      return;
    }
    const discard = await confirmDialog({
      title: 'Unsaved Changes',
      body: 'You have unsaved changes. Are you sure you want to close without saving?',
      confirmText: 'Discard Changes',
      confirmVariant: 'danger',
    });
    if (discard) close();
  };

  /**
   * The "Save and Close" button handler. Submits and, only on success,
   * invokes `close`. On failure the dialog stays open with the error
   * toast already fired by `submit()`.
   */
  saveAndClose = async (close: () => void): Promise<void> => {
    await this.submit();
    if (this.savingError === null) close();
  };

  /**
   * Save the current entity (the injected `save` callback reads whatever it
   * needs off it — request body, original identity). No-ops if the entity
   * hasn't loaded yet OR if a save is already in flight (concurrency guard).
   * On success: reinitializes the entity with the server's response and fires
   * the success toast. On failure: stores the error in `_savingState` and
   * fires an error toast.
   */
  submit = async (): Promise<void> => {
    const state = this._state.get();
    if (state === 'loading' || state instanceof Error) return;
    if (this._savingState.get() === 'saving') return;

    this._savingState.set('saving');
    try {
      const saved = await this._save(state);
      state.reinitialize(saved);
      this._savingState.set('idle');
      toast.success(this._options.successMessage);
    } catch (err) {
      const errObj = toError(err);
      this._savingState.set(errObj);
      const prefix = this._options.errorMessagePrefix ?? 'Save failed';
      toast.error(`${prefix}: ${errObj.message}`);
    }
  };

  get state(): T | 'loading' | Error {
    return this._state.get();
  }

  get isLoading(): boolean {
    return this._state.get() === 'loading';
  }

  get loadError(): Error | null {
    const s = this._state.get();
    return s instanceof Error ? s : null;
  }

  get entity(): T | null {
    const s = this._state.get();
    return s === 'loading' || s instanceof Error ? null : s;
  }

  get isSaving(): boolean {
    return this._savingState.get() === 'saving';
  }

  get savingError(): Error | null {
    const s = this._savingState.get();
    return s instanceof Error ? s : null;
  }

  /**
   * Whether the dialog is currently in create mode. Once the entity
   * has loaded the model delegates to `entity.isCreating` (so a future
   * Save-without-Close flips this to false after a successful save).
   * Before that, falls back to the `initialMode` option.
   */
  get isCreating(): boolean {
    const entity = this.entity;
    return entity ? entity.isCreating : this._options.initialMode === 'create';
  }
}
